/**
 * McSticky: Handles sticky header behavior using state classes on document.body.
 * States:
 * - default (no class): Static position
 * - mc-sticky-scrolled: User has scrolled past the header stack
 * - mc-sticky-up: User is scrolling up (sticky visible)
 * - mc-sticky-down: User is scrolling down (sticky hidden)
 */
const McSticky = (function () {
  const CLASSES = {
    scrolled: "mc-sticky-scrolled",
    up: "mc-sticky-up",
    down: "mc-sticky-down",
  };

  const SCROLL_DELTA = 8;
  let lastHandledScrollTop = 0;
  let threshold = 100;
  let rafId = null;
  let measureRafId = null;

  function init() {
    measureHeader();
    lastHandledScrollTop = getScrollTop();
    handleScrollState(lastHandledScrollTop, true);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", scheduleMeasure, { passive: true });

    if ("ResizeObserver" in window) {
      const resizeObserver = new ResizeObserver(scheduleMeasure);
      const topbar = document.querySelector(".mc-topbar");
      const navbar = document.querySelector(".mc-navbar");
      if (topbar) resizeObserver.observe(topbar);
      if (navbar) resizeObserver.observe(navbar);
    }
  }

  function getScrollTop() {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  function setRootVar(name, value) {
    document.documentElement.style.setProperty(name, `${value}px`);
  }

  function measureHeader() {
    const topbar = document.querySelector(".mc-topbar");
    const navbar = document.querySelector(".mc-navbar");
    const topbarHeight = topbar
      ? Math.ceil(topbar.getBoundingClientRect().height)
      : 0;
    const navbarHeight = navbar
      ? Math.ceil(navbar.getBoundingClientRect().height)
      : 0;
    const headerHeight = topbarHeight + navbarHeight;

    setRootVar("--mc-sticky-topbar-height", topbarHeight);
    setRootVar("--mc-sticky-navbar-height", navbarHeight);
    setRootVar("--mc-sticky-header-height", headerHeight);

    threshold = Math.max(100, headerHeight + 16);
  }

  function scheduleMeasure() {
    if (measureRafId) return;

    measureRafId = requestAnimationFrame(() => {
      measureHeader();
      handleScrollState(getScrollTop(), true);
      measureRafId = null;
    });
  }

  function onScroll() {
    if (rafId) return;

    rafId = requestAnimationFrame(() => {
      handleScrollState(getScrollTop(), false);
      rafId = null;
    });
  }

  function handleScrollState(scrollTop, force) {
    if (scrollTop < 0) return;

    const body = document.body;
    const delta = scrollTop - lastHandledScrollTop;

    if (!force && Math.abs(delta) < SCROLL_DELTA) return;

    if (scrollTop <= threshold) {
      body.classList.remove(CLASSES.scrolled, CLASSES.up, CLASSES.down);
      lastHandledScrollTop = scrollTop;
      return;
    }

    body.classList.add(CLASSES.scrolled);

    if ((force && body.classList.contains(CLASSES.up)) || delta < 0) {
      body.classList.remove(CLASSES.down);
      body.classList.add(CLASSES.up);
    } else if (force || delta > 0) {
      body.classList.remove(CLASSES.up);
      body.classList.add(CLASSES.down);
    }

    lastHandledScrollTop = scrollTop;
  }

  return { init, measureHeader };
})();

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", McSticky.init);
} else {
  McSticky.init();
}
